import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useStore from '../store/useStore';
import { kpisAPI, reportsAPI } from '../services/api';
import ReportDisplay from './widgets/ReportDisplay';
import DrillModal from './widgets/DrillModal';
import { formatKpiValue, describeChange, STATUS_LABEL, CADENCE_LABEL, PERIOD_LABEL, PREVIOUS_LABEL, kpiDrillQuery } from '../utils/kpiFormat';
import { rowsToDisplay } from '../utils/cubeQuery';
import '../styles/Scorecard.css';

const MAX_BREAKDOWNS = 4;
// More are tried than shown, because one with a single row says nothing.
const MAX_BREAKDOWN_CANDIDATES = 8;
// Identifiers and codes, and SLA results that would only restate the measure.
const NOT_A_BREAKDOWN = /(^|_)(id|key|number|ref|code|domain|state)$|_id_value$/;
const BLANK_LABEL = '(blank)';

/** The rows for a breakdown's chart, with blank categories named, and the measure's column. */
function breakdownRows(data, annotation, measure) {
  const rows = rowsToDisplay(data, annotation);
  const measureLabel = annotation?.measures?.[measure]?.shortTitle || annotation?.measures?.[measure]?.title;
  const named = rows.map((row) => Object.fromEntries(Object.entries(row).map(([k, v]) => [k, v === null || v === '' ? BLANK_LABEL : v])));
  const valueColumn = rows[0] && (Object.keys(rows[0]).find((k) => k === measureLabel) || Object.keys(rows[0]).find((k) => typeof rows[0][k] === 'number'));
  return { rows: named, valueColumn };
}

/**
 * Generated page for one KPI: the stat against target, the trend over the
 * last periods, breakdowns by the measure's string dimensions, and the
 * definition and targets behind it.
 */
const KpiPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentCompany = useStore((state) => state.currentCompany);
  const user = useStore((state) => state.user);
  const companyId = currentCompany?.id;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [result, setResult] = useState(null);
  const [breakdowns, setBreakdowns] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(false);

  useEffect(() => {
    if (!companyId || !id) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const res = await kpisAPI.evaluateOne(id, companyId);
        if (!res?.success) throw new Error(res?.error || 'Failed to load KPI');
        if (cancelled) return;
        setResult(res.data);

        // Breakdowns by the measure's string dimensions.
        const meta = await reportsAPI.getMeta(companyId);
        const cubeName = res.data.kpi.measure.split('.')[0];
        const cube = (meta?.data?.cubes || []).find((c) => c.name === cubeName);
        // Breakdowns by categorical dimensions: skip the row identifier and id-like columns.
        const pk = cube?.meta?.primaryKeyColumn;
        const dims = (cube?.dimensions || [])
          .filter((d) => d.type === 'string')
          .filter((d) => {
            const col = d.name.split('.').pop();
            return col !== pk && !NOT_A_BREAKDOWN.test(col);
          })
          .slice(0, MAX_BREAKDOWN_CANDIDATES);
        const td = res.data.kpi.timeDimension;
        const queries = dims.map((d) => ({
          dimension: d,
          query: {
            measures: [res.data.kpi.measure],
            dimensions: [d.name],
            ...(td ? { timeDimensions: [{ dimension: td, dateRange: res.data.period.dateRange ?? res.data.period.range }] } : {}),
            // Without this, categories with no value sort first and fill the rows, leaving nothing to chart.
            filters: [...(res.data.kpi.filters || []), { member: res.data.kpi.measure, operator: 'set' }],
            order: { [res.data.kpi.measure]: 'desc' },
            limit: 8,
          },
        }));
        const answers = await Promise.all(queries.map((q) => reportsAPI.query(companyId, q.query).catch(() => null)));
        if (cancelled) return;
        setBreakdowns(queries
          .map((q, i) => ({
            dimension: q.dimension,
            ...(answers[i]?.success ? breakdownRows(answers[i].data.data, answers[i].data.annotation, res.data.kpi.measure) : { rows: [] }),
          }))
          .filter((b) => b.rows.length >= 2)
          .slice(0, MAX_BREAKDOWNS));
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load KPI');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, id]);

  if (loading) return <div className="kpi-page"><div className="loading-spinner" /></div>;
  if (error || !result) {
    return (
      <div className="kpi-page">
        <div className="error-message">{error || 'KPI not found'}</div>
        <button className="btn-secondary" onClick={() => navigate('/')}>Back to dashboard</button>
      </div>
    );
  }

  const { kpi, value, previousValue, target, status, trend, period } = result;
  const periodLabel = kpi.timeDimension ? (period?.label || PERIOD_LABEL[kpi.cadence]) : 'As of today';
  const change = describeChange(result);
  const drillQuery = result.error ? null : kpiDrillQuery(result);
  const trendRows = trend.map((p) => ({
    period: p.period ? String(p.period).slice(0, 10) : '',
    [kpi.name]: p.value,
  }));

  return (
    <div className="kpi-page">
      <div className="page-header">
        <div>
          <h2>{kpi.name}</h2>
          <div className="kpi-meta">
            {kpi.department && <span>{kpi.department}</span>}
            <span>{CADENCE_LABEL[kpi.cadence]}</span>
            <span>{kpi.direction === 'lower' ? 'Lower is better' : 'Higher is better'}</span>
            {kpi.timing && <span>{kpi.timing}</span>}
            <code>{kpi.measure}</code>
          </div>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-secondary" onClick={() => navigate('/')}>Back to dashboard</button>
          {drillQuery && <button className="btn-secondary" onClick={() => setDrill(true)}>Show the rows behind this</button>}
          {isAdmin && <button className="btn-primary" onClick={() => navigate(`/admin/kpis?edit=${kpi.id}`)}>Edit KPI</button>}
        </div>
      </div>

      <div className="kpi-grid">
        <div className="kpi-panel">
          <h4>{periodLabel}</h4>
          <div className={`kpi-stat-value status-${status}`} data-testid="kpi-value">{formatKpiValue(value, kpi.format, kpi.unit)}</div>
          <dl className="kpi-stat-rows">
            <dt>Status</dt><dd><span className={`kpi-status-pill status-${status}`}>{STATUS_LABEL[status]}</span></dd>
            <dt>Target</dt><dd>{target === null || target === undefined ? 'none set' : formatKpiValue(target, kpi.format, kpi.unit)}</dd>
            {kpi.timeDimension && <><dt>{PREVIOUS_LABEL[kpi.cadence] || 'Previous'}</dt><dd>{formatKpiValue(previousValue, kpi.format, kpi.unit)}</dd></>}
            {change && <><dt>Change</dt><dd className={change.improving === null ? '' : change.improving ? 'scorecard-change improving' : 'scorecard-change worsening'}>{change.label}</dd></>}
            {result.error && <><dt>Error</dt><dd style={{ color: '#ff6b6b' }}>{result.error}</dd></>}
          </dl>
        </div>

        <div className="kpi-panel">
          <h4>Trend</h4>
          <div className="kpi-chart widget report-widget">
            <div className="widget-body report-body">
              {trendRows.length > 1
                ? <ReportDisplay rows={trendRows} config={{ displayType: 'line', formats: { [kpi.name]: kpi.format } }} />
                : <div className="scorecard-empty">{kpi.timeDimension ? 'Not enough history yet.' : 'Point-in-time KPI: no trend.'}</div>}
            </div>
          </div>
        </div>
      </div>

      {breakdowns.length > 0 && (
        <div className="kpi-panel" style={{ marginBottom: '1rem' }}>
          <h4>Breakdowns ({periodLabel})</h4>
          <div className="kpi-breakdowns">
            {breakdowns.map((b) => (
              <div key={b.dimension.name}>
                <div className="scorecard-dept" style={{ marginBottom: '0.4rem' }}>by {b.dimension.shortTitle || b.dimension.title || b.dimension.name}</div>
                <div className="kpi-chart widget report-widget">
                  <div className="widget-body report-body">
                    <ReportDisplay rows={b.rows} config={{ displayType: 'bar', formats: { [b.valueColumn]: kpi.format } }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="kpi-panel">
        <h4>Definition</h4>
        <dl className="kpi-stat-rows">
          <dt>Measure</dt><dd><code>{kpi.measure}</code></dd>
          <dt>Time dimension</dt><dd>{kpi.timeDimension ? <code>{kpi.timeDimension}</code> : 'none (point in time)'}</dd>
          <dt>Format</dt><dd>{kpi.format}{kpi.unit ? ` (${kpi.unit})` : ''}</dd>
          {kpi.description && <><dt>Description</dt><dd>{kpi.description}</dd></>}
          {kpi.sourceSystem && <><dt>Source</dt><dd>{kpi.sourceSystem}</dd></>}
          {kpi.owner && <><dt>Owner</dt><dd>{kpi.owner}</dd></>}
          <dt>Targets</dt>
          <dd>
            {(kpi.targets || []).length === 0 ? 'none' : (
              <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                {kpi.targets.map((t) => (
                  <li key={t.id}>{t.periodType === 'default' ? 'Default' : `${t.periodType} starting ${t.periodStart}`}: {formatKpiValue(t.target, kpi.format, kpi.unit)}</li>
                ))}
              </ul>
            )}
          </dd>
        </dl>
      </div>
      {drill && drillQuery && <DrillModal companyId={companyId} row={{}} query={drillQuery} measure={kpi.measure} title={`${kpi.name} (${periodLabel})`} onClose={() => setDrill(false)} />}
    </div>
  );
};

export default KpiPage;
